import { ButtonIconText, FileDropzone } from '@/components';
import { Upload } from '@/design-system/icons';
import { cn } from '@/lib';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { validateFile } from '../../types';
import { DocumentDownloadButtons } from '../partials/DocumentDownloadButtons';
import { OriginalFileNameLabel } from './OriginalFileNameLabel';
import type { FileObjectRow } from './types';
import { FormFieldAny } from '@/components/FormField';

// Type definitions for component props
type FileMode = 'create' | 'detail';

interface SingleFileObjectProps {
  row: FileObjectRow;
  isLocked: boolean;
  mode: FileMode;
  onFileSelect: (rowId: string, file: File | null) => void;
  onValidationError: (rowId: string, error: string) => void;
  onOpen: (rowId: string, download: boolean) => Promise<void>;
  onUpload: (rowId: string, file: File) => void;
}

interface AvailableFileProps {
  row: FileObjectRow;
  isLocked: boolean;
  isViewing: boolean;
  isDownloading: boolean;
  uploadInputRef: React.RefObject<HTMLInputElement | null>;
  onView: () => void;
  onDownload: () => void;
  onReuploadChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface SelectedFileProps {
  row: FileObjectRow;
  isLocked: boolean;
  onReplaceClick: () => void;
}

interface SelectFileButtonProps {
  isLocked: boolean;
  uploadInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface UploadProgressProps {
  progress: number;
}

/**
 * SingleFileObject renders the file upload/view UI for one file object row.
 * Used by both create and detail sidebars. Derives its visual state from the
 * row's upload status and availability flags.
 */
export function SingleFileObject({
  row,
  isLocked,
  mode,
  onFileSelect,
  onValidationError,
  onOpen,
  onUpload,
}: SingleFileObjectProps) {
  const { t } = useTranslation();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [isViewing, setIsViewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isUploading = row.uploadStatus === 'uploading';
  const isAvailable = row.values.isAvailable === true;
  const hasLocalFile = row.file !== null;
  const isCreateMode = mode === 'create';

  const handleReuploadChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      onUpload(row.rowId, selectedFile);
    }
    event.target.value = '';
  };

  const handleView = async () => {
    setIsViewing(true);
    try {
      await onOpen(row.rowId, false);
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onOpen(row.rowId, true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <FormFieldAny
      label={t('documents.createFile.fields.file')}
      mandatory
      noValue={!((isCreateMode && hasLocalFile) || isAvailable)}
    >
      <div className="space-y-3">
        {isUploading ? (
          <UploadProgress progress={row.uploadProgress} />
        ) : isAvailable ? (
          <AvailableFile
            row={row}
            isLocked={isLocked}
            isViewing={isViewing}
            isDownloading={isDownloading}
            uploadInputRef={uploadInputRef}
            onView={handleView}
            onDownload={handleDownload}
            onReuploadChange={handleReuploadChange}
          />
        ) : isCreateMode && hasLocalFile ? (
          <SelectedFile
            row={row}
            isLocked={isLocked}
            onReplaceClick={() => onFileSelect(row.rowId, null)}
          />
        ) : isCreateMode ? (
          <FileDropzone
            file={row.file}
            onFileSelect={(file) => onFileSelect(row.rowId, file)}
            disabled={isLocked}
            prompt={t('documents.createFile.filePrompt')}
            validateFile={validateFile}
            onValidationError={(error) => onValidationError(row.rowId, error)}
          />
        ) : (
          <SelectFileButton
            isLocked={isLocked}
            uploadInputRef={uploadInputRef}
            onFileChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              onFileSelect(row.rowId, file);
              event.target.value = '';
            }}
          />
        )}

        {row.fileError ? (
          <p className="text-sm text-error">{row.fileError}</p>
        ) : null}
      </div>
    </FormFieldAny>
  );
}

// Helper component: Shows upload progress bar with percentage.
function UploadProgress({ progress }: UploadProgressProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-onsurface/70">{t('documents.createFile.upload.uploading')}</span>
        <span className="text-onsurface font-medium">{progress}%</span>
      </div>
      <div className="bg-surface-darker h-2 rounded-full">
        <div
          className="h-2 rounded-full transition-all bg-primary"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}


// Helper component: Shows view, download and reupload actions for an available file object.
function AvailableFile({
  row,
  isLocked,
  isViewing,
  isDownloading,
  uploadInputRef,
  onView,
  onDownload,
  onReuploadChange,
}: AvailableFileProps) {
  const { t } = useTranslation();
  const isBusy = isViewing || isDownloading;

  return (
    <div className="flex items-start gap-2 justify-between">
      <input
        ref={uploadInputRef}
        type="file"
        className="hidden"
        onChange={onReuploadChange}
        disabled={isLocked}
      />

      <div className="min-w-0 flex-1 space-y-2">
        <OriginalFileNameLabel originalFileName={row.values.originalFileName} />

        <DocumentDownloadButtons
          objectId={""}
          isViewing={isViewing}
          isDownloading={isDownloading}
          isBusy={isBusy}
          onView={onView}
          onDownload={onDownload}
          viewText={t('documents.detail.fileObjects.view')}
          viewLoadingText={t('app.loading')}
          downloadText={t('documents.detail.fileObjects.download')}
          downloadLoadingText={t('app.loading')}
        />
      </div>

      <div className="hidden sm:flex">
        <ButtonIconText
          iconName="upload"
          onClick={() => uploadInputRef.current?.click()}
          disabled={isBusy}
          className={cn(isViewing ? 'opacity-50' : '')}
        >
          {t('documents.detail.fileObjects.reupload')}
        </ButtonIconText>
      </div>

      <button
        type="button"
        onClick={() => uploadInputRef.current?.click()}
        disabled={isLocked || isBusy}
        className="inline-flex items-center justify-center rounded-sm border border-border bg-surface p-1.5 text-onsurface transition-colors hover:bg-surface-darker disabled:pointer-events-none disabled:opacity-50 sm:hidden"
        title={t('documents.detail.fileObjects.reupload')}
        aria-label={t('documents.detail.fileObjects.reupload')}
      >
        <Upload className="size-4" />
      </button>
    </div>
  );
}


// Helper component: Shows the locally selected file name with a Replace action in create mode.
function SelectedFile({ row, isLocked, onReplaceClick }: SelectedFileProps) {
  const { t } = useTranslation();
  return (
    <div className="border-border bg-surface-darker/40 flex items-center justify-between rounded-lg border p-3">
      <span className="text-onsurface text-sm truncate flex-1 min-w-0">{row.file?.name}</span>
      {!isLocked ? (
        <button
          type="button"
          onClick={onReplaceClick}
          className="text-primary text-sm font-medium shrink-0 ml-3"
        >
          {t('documents.createFile.replaceFile')}
        </button>
      ) : null}
    </div>
  );
}


// Helper component: Shows a hidden file input and a "Select file" button for detail mode new rows.
function SelectFileButton({ isLocked, uploadInputRef, onFileChange }: SelectFileButtonProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <input
        ref={uploadInputRef}
        type="file"
        className="hidden"
        onChange={onFileChange}
        disabled={isLocked}
      />
      <button
        type="button"
        onClick={() => uploadInputRef.current?.click()}
        disabled={isLocked}
        className="border-border bg-surface text-onsurface hover:bg-surface-darker/40 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
      >
        {t('documents.detail.fileObjects.selectFile')}
      </button>
    </div>
  );
}
